import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

import AddMyAddress from "../../UserProfileOverview/MyAddress/AddMyAddress/AddMyAddress";
import { getImageUrl } from "../../../../lib/get-image";
import styles from "./checkoutpayment.module.css";
import PaymentSection from "./PaymentSection";

import { useAddOrderMutation } from "../../../../queries/useOrderQuery";
import { useRemoveCartItem } from "../../../../queries/useCartQuery";
import { paymentOptions } from "../../../../constants/payment-options";
import { useUserAddressListQuery } from "../../../../queries/useUserAddress";

import {
  CART,
  USER_ORDERS,
} from "../../../../constants/query.constants";

import {
  payWithRazorpay,
  razorpayPaymentFailed,
  verifyRazorpayPayment,
} from "../../../../repositories/orders/orderRepository";

import CheckoutStepper from "../../Cart/Checkoutstepper";

const CheckoutPayment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const {
    data: addressList = [],
    isPending: isPendingAddress,
  } = useUserAddressListQuery();

  const [prescriptions, setPrescriptions] = useState({});
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [storedAddress, setStoredAddress] = useState(null);
  const [savedShipping, setSavedShipping] = useState(null);
  const [showAddressEdit, setShowAddressEdit] = useState(false);

  // =====================================================
  // SAFE DISPLAY VALUE
  // =====================================================
  const getDisplayValue = (value) => {
    if (value == null) return "";

    if (typeof value === "object") {
      return (
        value.name ||
        value.title ||
        value.label ||
        value.value ||
        ""
      );
    }

    return String(value);
  };

  // =====================================================
  // GET VENDOR ID
  //
  // Searches the complete object recursively.
  //
  // This handles:
  //
  // item.vendor_id
  // item.vendor.id
  // item.product.vendor_id
  // item.product.vendor.id
  // item.variant.vendor_id
  // item.product_variant_item.vendor_id
  // item.product_variant.vendor_id
  // and deeply nested vendor data.
  // =====================================================
  const getVendorId = (item = {}) => {
    const visited = new WeakSet();

    const findVendorId = (value, parentKey = "") => {
      if (value == null) {
        return "";
      }

      if (
        typeof value === "object" &&
        value !== null
      ) {
        if (visited.has(value)) {
          return "";
        }

        visited.add(value);
      }

      // -----------------------------------------------
      // DIRECT VENDOR ID KEYS
      // -----------------------------------------------
      if (
        typeof value === "object" &&
        value !== null
      ) {
        const directVendorKeys = [
          "vendor_id",
          "vendorId",
          "vendorID",
        ];

        for (const key of directVendorKeys) {
          if (
            value[key] !== undefined &&
            value[key] !== null &&
            value[key] !== ""
          ) {
            return value[key];
          }
        }
      }

      // -----------------------------------------------
      // VENDOR OBJECT
      // -----------------------------------------------
      if (
        typeof value === "object" &&
        value !== null
      ) {
        const vendorValue = value.vendor;

        if (
          vendorValue &&
          typeof vendorValue === "object"
        ) {
          if (
            vendorValue.id !== undefined &&
            vendorValue.id !== null &&
            vendorValue.id !== ""
          ) {
            return vendorValue.id;
          }

          if (
            vendorValue.vendor_id !== undefined &&
            vendorValue.vendor_id !== null &&
            vendorValue.vendor_id !== ""
          ) {
            return vendorValue.vendor_id;
          }

          if (
            vendorValue.vendorId !== undefined &&
            vendorValue.vendorId !== null &&
            vendorValue.vendorId !== ""
          ) {
            return vendorValue.vendorId;
          }
        }
      }

      // -----------------------------------------------
      // RECURSIVE SEARCH
      // -----------------------------------------------
      if (
        typeof value === "object" &&
        value !== null
      ) {
        for (const [key, child] of Object.entries(value)) {
          if (
            child &&
            typeof child === "object"
          ) {
            const result = findVendorId(
              child,
              key
            );

            if (result !== "") {
              return result;
            }
          }
        }
      }

      return "";
    };

    return findVendorId(item);
  };

  // =====================================================
  // GET CORRECT PRODUCT PRICE
  // =====================================================
 const getProductPrice = (
  product = {},
  item = {}
) => {
  const selectedVariant =
    item?.variant ||
    product?.variants?.[0] ||
    {};

  // =====================================================
  // OFFER PRICE
  // Supports both:
  // variant_offer_price  -> Product Details
  // offer_price          -> Cart/normalized checkout
  // =====================================================
  const variantOfferPrice = Number(
    selectedVariant?.offer_price ??
    selectedVariant?.variant_offer_price ??
    0
  );

  // =====================================================
  // REGULAR PRICE
  // =====================================================
  const variantPrice = Number(
    selectedVariant?.price ??
    selectedVariant?.variant_price ??
    0
  );

  const productOfferPrice = Number(
    product?.offer_price ?? 0
  );

  const productPrice = Number(
    product?.price ?? 0
  );

  const itemOfferPrice = Number(
    item?.offer_price ?? 0
  );

  const itemPrice = Number(
    item?.price ??
    item?.unit_price ??
    0
  );

  // =====================================================
  // PRIORITY
  // =====================================================

  // 1. Selected variant offer price
  if (variantOfferPrice > 0) {
    return variantOfferPrice;
  }

  // 2. Product offer price
  if (productOfferPrice > 0) {
    return productOfferPrice;
  }

  // 3. Selected variant regular price
  if (variantPrice > 0) {
    return variantPrice;
  }

  // 4. Product regular price
  if (productPrice > 0) {
    return productPrice;
  }

  // 5. Cart item offer price
  if (itemOfferPrice > 0) {
    return itemOfferPrice;
  }

  // 6. Cart item price
  return itemPrice;
};

  // =====================================================
  // LOAD CHECKOUT DATA
  // =====================================================
  useEffect(() => {
    if (isPendingAddress) {
      return;
    }

    try {
      const storedAddressData =
        localStorage.getItem("checkoutAddress");

      const storedShippingData =
        localStorage.getItem("shipping-rules");

      const address = storedAddressData
        ? JSON.parse(storedAddressData)
        : null;

      const cartShipping = storedShippingData
        ? JSON.parse(storedShippingData)
        : null;

      // =================================================
      // ADDRESS VALIDATION
      // =================================================
      if (!address) {
        localStorage.removeItem(
          "checkoutAddress"
        );

        setStoredAddress(null);

        toast.error(
          "Please select a delivery address"
        );

        navigate("/cart");

        return;
      }

      if (!address.id) {
        localStorage.removeItem(
          "checkoutAddress"
        );

        setStoredAddress(null);

        toast.error(
          "Please select a valid delivery address"
        );

        navigate("/cart");

        return;
      }

      // =================================================
      // CHECK ADDRESS AGAINST CURRENT ADDRESS LIST
      // =================================================
      const validAddress = addressList.find(
        (item) =>
          String(item.id) ===
          String(address.id)
      );

      if (!validAddress) {
        localStorage.removeItem(
          "checkoutAddress"
        );

        setStoredAddress(null);

        toast.error(
          "Selected address is no longer available"
        );

        navigate("/cart");

        return;
      }

      setStoredAddress(validAddress);

      // =================================================
      // NORMAL CART CHECKOUT SHIPPING
      // =================================================
      if (!location.state?.buyNow) {
        if (!cartShipping) {
          toast.error(
            "Shipping data missing"
          );

          navigate("/cart");

          return;
        }

        setSavedShipping(cartShipping);

        return;
      }

      // =================================================
      // BUY NOW SHIPPING
      // =================================================
      const buyNowProduct =
        location.state?.product;

      const buyNowQuantity = Number(
        location.state?.quantity || 1
      );

      if (!buyNowProduct) {
        toast.error(
          "Buy Now product data missing"
        );

        navigate("/cart");

        return;
      }

      const productPrice =
        getProductPrice(
          buyNowProduct
        );

      const buyNowSubtotal =
        productPrice * buyNowQuantity;

      const buyNowShipping =
        buyNowSubtotal >= 1000
          ? {
              name: "Express Delivery",
              description:
                "Free delivery",
              cost: 0,
            }
          : {
              name: "Standard Delivery",
              description:
                "3–5 business days",
              cost: 100,
            };

      console.log(
        "BUY NOW SUBTOTAL:",
        buyNowSubtotal
      );

      console.log(
        "BUY NOW SHIPPING:",
        buyNowShipping
      );

      setSavedShipping(
        buyNowShipping
      );
    } catch (error) {
      console.error(
        "Checkout data error:",
        error
      );

      localStorage.removeItem(
        "checkoutAddress"
      );

      setStoredAddress(null);

      toast.error(
        "Invalid checkout data"
      );

      navigate("/cart");
    }
  }, [
    addressList,
    isPendingAddress,
    navigate,
    location.state,
  ]);

  // =====================================================
  // EDIT DELIVERY ADDRESS
  // =====================================================
  const handleEditAddress = () => {
    setShowAddressEdit(true);
  };

  const handleCloseAddressEdit = () => {
    setShowAddressEdit(false);
  };

  const handleAddressUpdate = (response) => {
    console.log(
      "UPDATED ADDRESS:",
      response
    );

    const updatedAddress =
      response?.data || response;

    if (!updatedAddress?.id) {
      toast.error(
        "Address updated but data not found"
      );

      return;
    }

    setStoredAddress(
      updatedAddress
    );

    localStorage.setItem(
      "checkoutAddress",
      JSON.stringify(updatedAddress)
    );

    setShowAddressEdit(false);
  };

  // =====================================================
  // BUY NOW ITEM
  // =====================================================
const buyNowItem = location.state?.buyNow
  ? [
      {
        id: location.state.product?.id,

        product_id: location.state.product?.id,

        quantity: Number(location.state.quantity || 1),

        product: location.state.product,

        // =====================================================
        // SELECTED VARIANT
        // Normalize Product Details variant fields
        // to the same fields used by Cart checkout.
        // =====================================================
        variant: {
          ...location.state.selectedVariant,

          // Original price
          price:
            location.state.selectedVariant?.variant_price ??
            location.state.product?.price ??
            0,

          // Offer price
          offer_price:
            location.state.selectedVariant
              ?.variant_offer_price ??
            location.state.product?.offer_price ??
            null,

          // Size
          size:
            location.state.selectedVariant?.variant_size ??
            location.state.selectedSize ??
            "",

          size_name:
            location.state.selectedVariant?.variant_size ??
            location.state.selectedSize ??
            "",

          // Color
          color_name:
            location.state.selectedVariant?.color_name ??
            location.state.selectedColor ??
            "",

          color:
            location.state.selectedVariant?.color_name ??
            location.state.selectedColor ??
            "",

          // Variant ID
          id:
            location.state.selectedVariant?.id,

          // SKU
          sku:
            location.state.selectedVariant?.variant_sku ||
            "",
        },

        // =====================================================
        // VARIANT ID
        // =====================================================
        productVariantItemId:
          location.state.selectedVariant?.id,

        product_variant_item_id:
          location.state.selectedVariant?.id,

        // =====================================================
        // SELECTED SIZE / COLOR
        // =====================================================
        selectedSize:
          location.state.selectedSize ||
          location.state.selectedVariant?.variant_size ||
          "",

        selectedColor:
          location.state.selectedColor ||
          location.state.selectedVariant?.color_name ||
          "",

        // =====================================================
        // VENDOR
        // =====================================================
        vendor_id:
          location.state.product?.vendor_id ||
          location.state.product?.vendorId ||
          location.state.product?.vendorID ||
          location.state.product?.vendor?.id ||
          location.state.product?.vendor?.vendor_id ||
          location.state.product?.vendor?.vendorId ||
          location.state.product?.seller_id ||
          location.state.product?.sellerId ||
          location.state.selectedVariant?.vendor_id ||
          location.state.selectedVariant?.vendorId ||
          location.state.selectedVariant?.vendor?.id ||
          location.state.selectedVariant?.vendor?.vendor_id ||
          location.state.selectedVariant?.seller_id ||
          "",
      },
    ]
  : null;

  // =====================================================
  // SELECTED CART ITEMS
  // =====================================================
  let selectedCartItems = [];

  try {
    selectedCartItems = buyNowItem
      ? buyNowItem
      : JSON.parse(
          localStorage.getItem(
            "selectedCheckoutItems"
          )
        ) || [];
  } catch (error) {
    console.error(
      "Selected checkout items parsing error:",
      error
    );

    selectedCartItems = [];
  }

  // =====================================================
  // DEBUG SELECTED CART ITEMS
  // =====================================================
  useEffect(() => {
    console.log(
      "======================================"
    );

    console.log(
      "🛒 SELECTED CHECKOUT ITEMS:"
    );

    console.log(
      selectedCartItems
    );

    selectedCartItems.forEach(
      (item, index) => {
        console.log(
          `🛒 ITEM ${index}:`,
          item
        );

        console.log(
          `🛒 ITEM ${index} VENDOR ID:`,
          getVendorId(item)
        );
      }
    );

    console.log(
      "======================================"
    );
  }, [selectedCartItems]);

  const selectedCount =
    selectedCartItems.length;

  // =====================================================
  // SUBTOTAL
  // =====================================================
  const subtotal =
    selectedCartItems.reduce(
      (acc, item) => {
        const price =
          getProductPrice(
            item?.product || {},
            item
          );

        const quantity = Number(
          item?.quantity ||
            item?.qty ||
            1
        );

        console.log(
          "CHECKOUT ITEM:",
          item
        );

        console.log(
          "CHECKOUT PRICE:",
          price
        );

        console.log(
          "CHECKOUT QUANTITY:",
          quantity
        );

        return (
          acc +
          price * quantity
        );
      },
      0
    );

  // =====================================================
  // TOTAL QUANTITY
  // =====================================================
  const totalQty =
    selectedCartItems.reduce(
      (acc, item) =>
        acc +
        Number(
          item.quantity ||
            item.qty ||
            1
        ),
      0
    );

  // =====================================================
  // SHIPPING
  // =====================================================
  const shipping = Number(
    typeof savedShipping?.cost ===
      "object"
      ? savedShipping?.cost
          ?.amount ||
          savedShipping?.cost
            ?.value ||
          0
      : savedShipping?.cost || 0
  );

  const tax = 0;

  const total =
    subtotal + shipping;

  const isCartPending = false;

  // =====================================================
  // ORDER SUMMARY
  // =====================================================
  const orderSummary = {
    subtotal,
    shipping,
    tax,
    total,
    totalQty,
  };

  // =====================================================
  // MUTATIONS
  // =====================================================
  const {
    mutateAsync: placeOrder,
    isPending:
      isOrderPlacePending,
  } = useAddOrderMutation();

  const {
    mutateAsync: removeCartItem,
  } = useRemoveCartItem();

  // =====================================================
  // CLEAR CART
  // =====================================================
  const clearCart = async () => {
    // Buy Now does not remove cart items
    if (buyNowItem) {
      return;
    }

    for (const item of selectedCartItems) {
      await removeCartItem(
        item.id
      );
    }

    queryClient.invalidateQueries({
      queryKey: [CART],
    });

    queryClient.invalidateQueries({
      queryKey: [USER_ORDERS],
    });
  };

  // =====================================================
  // PLACE ORDER
  // =====================================================
  const handlePlaceOrder =
    async () => {
      if (!selectedPayment) {
        toast.error(
          "Select payment method!"
        );

        return;
      }

      try {
        // ===============================================
        // BASIC RAZORPAY PAYLOAD
        // ===============================================
     const payload = {
  sub_total: Number(subtotal),

  amount: Number(total),

  product_qty: Number(totalQty),

  order_address: storedAddress,

  shipping_method: savedShipping,

  products: selectedCartItems.map((item) => {
    const vendorId = getVendorId(item);

    const productId =
      item.product_id ||
      item.product?.id ||
      null;

    const productVariantItemId =
      item?.productVariantItemId ||
      item?.product_variant_item_id ||
      item?.variant?.id ||
      null;

    const productName =
      getDisplayValue(
        item.product_name ||
          item.product?.name ||
          item.name
      ) || "Product";

    const unitPrice =
      getProductPrice(
        item?.product || {},
        item
      );

    const quantity = Number(
      item.quantity ||
        item.qty ||
        1
    );

    return {
      product_id: productId,

      ...(productVariantItemId
        ? {
            product_variant_item_id:
              productVariantItemId,
          }
        : {}),

      ...(vendorId
        ? {
            vendor_id: vendorId,
          }
        : {}),

      product_name: productName,

      unit_price: Number(unitPrice),

      qty: quantity,
    };
  }),
};

        // ===============================================
        // FORM DATA
        // ===============================================
        const formData =
          new FormData();

        // ===============================================
        // BASIC
        // ===============================================
        formData.append(
          "sub_total",
          String(
            Number(
              subtotal || 0
            )
          )
        );

        formData.append(
          "amount",
          String(
            Number(
              total || 0
            )
          )
        );

        formData.append(
          "product_qty",
          String(
            Number(
              totalQty || 0
            )
          )
        );

        // ===============================================
        // ORDER ADDRESS
        // ===============================================
        Object.entries(
          storedAddress || {}
        ).forEach(
          ([key, value]) => {
            let formattedValue =
              value;

            if (
              typeof value ===
                "object" &&
              value !== null
            ) {
              formattedValue =
                value.id ??
                value.name ??
                "";
            }

            formData.append(
              `order_address[${key}]`,
              String(
                formattedValue ?? ""
              )
            );
          }
        );

        // ===============================================
        // SHIPPING
        // ===============================================
        Object.entries(
          savedShipping || {}
        ).forEach(
          ([key, value]) => {
            let formattedValue =
              value;

            if (
              typeof value ===
                "object" &&
              value !== null
            ) {
              formattedValue =
                value.id ??
                value.name ??
                value.amount ??
                "";
            }

            formData.append(
              `shipping_method[${key}]`,
              String(
                formattedValue ?? ""
              )
            );
          }
        );

        // ===============================================
        // PRODUCTS
        // ===============================================
        selectedCartItems.forEach(
          (item, index) => {
            const productId =
              item.product_id ||
              item.product?.id ||
              "";

            // =========================================
            // GET VENDOR ID
            // =========================================
            const vendorId =
              getVendorId(item);

            const productName =
              getDisplayValue(
                item.product_name ||
                  item.product?.name ||
                  item.name
              ) || "Product";

            const unitPrice =
              getProductPrice(
                item?.product ||
                  {},
                item
              );

            const quantity =
              Number(
                item.quantity ||
                  item.qty ||
                  1
              );

            // =========================================
            // DEBUG
            // =========================================
            console.log(
              "======================================"
            );

            console.log(
              "🛒 ITEM:",
              item
            );

            console.log(
              "🛒 FULL ITEM JSON:",
              JSON.stringify(
                item,
                null,
                2
              )
            );

            console.log(
              "✅ PRODUCT ID:",
              productId
            );

            console.log(
              "✅ VENDOR ID:",
              vendorId
            );

            console.log(
              "✅ PRODUCT NAME:",
              productName
            );

            console.log(
              "✅ UNIT PRICE:",
              unitPrice
            );

            console.log(
              "✅ QUANTITY:",
              quantity
            );

            console.log(
              "======================================"
            );

            // =========================================
            // VENDOR IS REQUIRED
            // =========================================
          // =========================================
// APPEND PRODUCT DATA
// =========================================


// =========================================
// VENDOR ID
// =========================================
// Vendor ID is optional here.
// If it exists, send it.
// If it does not exist, DO NOT STOP checkout.
formData.append(
  `products[${index}][product_id]`,
  String(productId)
);

// =========================================
// PRODUCT VARIANT ITEM ID
// =========================================
const productVariantItemId =
  item?.productVariantItemId ||
  item?.product_variant_item_id ||
  item?.variant?.id ||
  "";

if (
  productVariantItemId !== undefined &&
  productVariantItemId !== null &&
  String(productVariantItemId).trim() !== ""
) {
  formData.append(
    `products[${index}][product_variant_item_id]`,
    String(productVariantItemId)
  );
}

// =========================================
// VENDOR ID
// =========================================
if (
  vendorId !== undefined &&
  vendorId !== null &&
  String(vendorId).trim() !== ""
) {
  formData.append(
    `products[${index}][vendor_id]`,
    String(vendorId)
  );
}

formData.append(
  `products[${index}][product_name]`,
  productName
);

formData.append(
  `products[${index}][unit_price]`,
  String(unitPrice)
);

formData.append(
  `products[${index}][qty]`,
  String(quantity)
);
          }
        );

        // ===============================================
        // PRESCRIPTION IMAGE
        // ===============================================
        Object.entries(
          prescriptions
        ).forEach(
          ([productId, files]) => {
            files.forEach(
              (
                fileObj,
                index
              ) => {
                formData.append(
                  `prescriptions[${productId}][${index}]`,
                  fileObj.file
                );
              }
            );
          }
        );

        // ===============================================
        // RAZORPAY
        // ===============================================
        if (
          selectedPayment ===
          "razorpay"
        ) {
          console.log(
            "RAZORPAY PAYLOAD:",
            payload
          );

          const res =
            await payWithRazorpay(
              payload
            );

          if (
            !res?.order ||
            !res?.key
          ) {
            toast.error(
              "Razorpay order creation failed"
            );

            return;
          }

          const {
            order,
            key,
          } = res;

          const options = {
            key,

            amount:
              order.amount,

            currency:
              order.currency,

            name:
              "Your Store",

            description:
              "Order Payment",

            order_id:
              order.id,

            handler:
              async (
                paymentResponse
              ) => {
                try {
                  const verifyRes =
                    await verifyRazorpayPayment(
                      {
                        razorpay_payment_id:
                          paymentResponse.razorpay_payment_id,

                        razorpay_order_id:
                          paymentResponse.razorpay_order_id,

                        razorpay_signature:
                          paymentResponse.razorpay_signature,
                      }
                    );

                  console.log(
                    "VERIFY RESPONSE:",
                    verifyRes
                  );

                  toast.success(
                    "Payment successful 🎉"
                  );

                  navigate(
                    "/checkout/order-success",
                    {
                      state: {
                        payment_id:
                          paymentResponse.razorpay_payment_id,

                        payment_method:
                          "Razorpay",

                        orderId:
                          order?.id,

                        invoice_id:
                          order?.invoice_id,
                      },
                    }
                  );
                } catch (
                  err
                ) {
                  console.error(
                    "Payment verification error:",
                    err
                  );

                  toast.error(
                    "Payment verification failed"
                  );
                }
              },

            modal: {
              ondismiss:
                async () => {
                  console.log(
                    "RAZORPAY POPUP CLOSED"
                  );

                  try {
                    const res =
                      await razorpayPaymentFailed(
                        {
                          razorpay_order_id:
                            order.id,

                          reason:
                            "cancelled_by_user",
                        }
                      );

                    console.log(
                      "FAILURE API RESPONSE:",
                      res
                    );
                  } catch (
                    err
                  ) {
                    console.error(
                      "FAILURE API ERROR:",
                      err
                    );
                  }

                  toast.error(
                    "Payment cancelled"
                  );
                },
            },

            prefill: {
              name:
                getDisplayValue(
                  storedAddress?.name ||
                    storedAddress?.full_name
                ),

              email:
                getDisplayValue(
                  storedAddress?.email
                ),

              contact:
                getDisplayValue(
                  storedAddress?.phone
                ),
            },

            theme: {
              color:
                "#3399cc",
            },
          };

          const razor =
            new window.Razorpay(
              options
            );

          razor.on(
            "payment.failed",
            async (
              response
            ) => {
              console.log(
                "PAYMENT FAILED EVENT",
                response
              );

              try {
                const res =
                  await razorpayPaymentFailed(
                    {
                      razorpay_order_id:
                        order.id,

                      razorpay_payment_id:
                        response
                          ?.error
                          ?.metadata
                          ?.payment_id ||
                        null,

                      reason:
                        response
                          ?.error
                          ?.description ||
                        "payment_failed",
                    }
                  );

                console.log(
                  "FAILURE API RESPONSE:",
                  res
                );
              } catch (
                err
              ) {
                console.error(
                  "FAILURE API ERROR:",
                  err
                );
              }
            }
          );

          razor.open();

          return;
        }

        // ===============================================
        // CASH ON DELIVERY
        // ===============================================
        if (
          selectedPayment ===
          "cod"
        ) {
          console.log(
            "COD FORM DATA READY"
          );

          // Debug FormData before sending
          for (const [
            key,
            value,
          ] of formData.entries()) {
            console.log(
              "FORM DATA:",
              key,
              value
            );
          }

         const response =
  await placeOrder(
    payload
  );

          console.log(
            "COD ORDER RESPONSE:",
            response
          );

          const invoiceId =
            response?.order
              ?.invoice_id;

          const orderId =
            response?.order?.id;

          await clearCart();

          toast.success(
            "COD order placed successfully 🎉"
          );

          navigate(
            "/checkout/success",
            {
              state: {
                invoiceId,
                orderId,
              },
            }
          );

          return;
        }
      } catch (err) {
        console.log(
          "🔥 FULL ERROR:",
          err
        );

        console.log(
          "🔥 STATUS:",
          err?.response?.status
        );

      console.log(
  "🔥 API ERROR DATA:",
  JSON.stringify(
    err?.response?.data,
    null,
    2
  )
);

console.log(
  "🔥 API ERROR DETAIL:",
  JSON.stringify(
    err?.response?.data?.detail,
    null,
    2
  )
);

        console.log(
          "🔥 MESSAGE:",
          err?.message
        );

        toast.error(
          err?.response?.data
            ?.message ||
            err?.response?.data
              ?.error ||
            err?.message ||
            "Order failed"
        );
      }
    };

  // =====================================================
  // LOADING
  // =====================================================
  if (
    !storedAddress ||
    !savedShipping
  ) {
    return null;
  }

  // =====================================================
  // UI
  // =====================================================
  return (
    <section
      className={styles.wrapper}
    >
      {/* =================================================
          EDIT DELIVERY ADDRESS POPUP
      ================================================= */}
      {showAddressEdit &&
        storedAddress && (
          <div
            className={
              styles.addressEditOverlay
            }
          >
            <div
              className={
                styles.addressEditPopup
              }
            >
              <AddMyAddress
                mode="edit"
                initialData={
                  storedAddress
                }
                addressId={
                  storedAddress?.id
                }
                onClose={
                  handleCloseAddressEdit
                }
                onSave={
                  handleAddressUpdate
                }
              />
            </div>
          </div>
        )}

      {/* =================================================
          CHECKOUT STEPPER
      ================================================= */}
      <div
        className={
          styles.checkoutHeader
        }
      >
        <div
          className={
            styles.bannerContent
          }
        >
          <span
            className={
              styles.bannerSmallText
            }
          >
            CHECKOUT
          </span>

          <h1>Payment</h1>

          <p>
            Review your order and
            choose your preferred
            payment method to
            complete your purchase.
          </p>
        </div>

        <CheckoutStepper
          currentStep={2}
        />
      </div>

      <div
        className={
          styles.contentContainer
        }
      >
        {/* =================================================
            LEFT COLUMN
        ================================================= */}
        <div
          className={
            styles.leftColumn
          }
        >
          {/* =================================================
              PAYMENT METHOD
          ================================================= */}
          <div
            className={
              styles.paymentCard
            }
          >
            <div
              className={
                styles.cardHeader
              }
            >
              <div>
                <span
                  className={
                    styles.sectionNumber
                  }
                ></span>

                <h2>
                  Payment Method
                </h2>
              </div>

              <span
                className={
                  styles.secureBadge
                }
              >
                🔒 Secure
              </span>
            </div>

            <p
              className={
                styles.cardDescription
              }
            >
              Choose your preferred
              payment method to
              complete your order.
            </p>

            <PaymentSection
              paymentOptions={
                paymentOptions
              }
              selectedPayment={
                selectedPayment
              }
              setSelectedPayment={
                setSelectedPayment
              }
            />
          </div>

          {/* =================================================
              ORDER REVIEW
          ================================================= */}
          <div
            className={
              styles.reviewCard
            }
          >
            <div
              className={
                styles.cardHeader
              }
            >
              <div>
                <span
                  className={
                    styles.sectionNumber
                  }
                ></span>

                <h2>
                  Order Review
                </h2>
              </div>
            </div>

            {/* =================================================
                DELIVERY ADDRESS
            ================================================= */}
            <div
              className={
                styles.reviewBlock
              }
            >
              <div
                className={
                  styles.reviewBlockHeader
                }
              >
                <h3>
                  Deliver to
                </h3>

                <button
                  type="button"
                  className={
                    styles.smallEditButton
                  }
                  onClick={
                    handleEditAddress
                  }
                >
                  Edit
                </button>
              </div>

              <div
                className={
                  styles.addressReview
                }
              >
                <strong>
                  {getDisplayValue(
                    storedAddress?.type ||
                      storedAddress?.address_type
                  ) ||
                    "Delivery Address"}
                </strong>

                <p>
                  {getDisplayValue(
                    storedAddress?.name ||
                      storedAddress?.full_name
                  )}
                </p>

                <p>
                  {getDisplayValue(
                    storedAddress?.address ||
                      storedAddress?.address_line1
                  )}
                </p>

                <p>
                  {getDisplayValue(
                    storedAddress?.city
                  )}

                  {storedAddress?.city &&
                  storedAddress?.state
                    ? ", "
                    : ""}

                  {getDisplayValue(
                    storedAddress?.state
                  )}
                </p>

                <p>
                  {getDisplayValue(
                    storedAddress?.pincode ||
                      storedAddress?.postal_code
                  )}
                </p>

                {storedAddress?.phone && (
                  <p>
                    Phone:{" "}
                    {getDisplayValue(
                      storedAddress.phone
                    )}
                  </p>
                )}
              </div>
            </div>

            {/* =================================================
                SHIPPING METHOD
            ================================================= */}
            <div
              className={
                styles.reviewBlock
              }
            >
              <div
                className={
                  styles.reviewBlockHeader
                }
              >
                <h3>
                  Shipping Method
                </h3>
              </div>

              <div
                className={
                  styles.shippingReview
                }
              >
                <div
                  className={
                    styles.shippingIcon
                  }
                >
                  🚚
                </div>

                <div
                  className={
                    styles.shippingDetails
                  }
                >
                  <strong>
                    {getDisplayValue(
                      savedShipping?.name ||
                        savedShipping?.title ||
                        savedShipping?.shipping_name
                    ) ||
                      "Standard Delivery"}
                  </strong>

                  <span>
                    {getDisplayValue(
                      savedShipping?.description ||
                        savedShipping?.delivery_time
                    ) ||
                      "3–5 business days"}
                  </span>
                </div>

                <strong
                  className={
                    styles.shippingPrice
                  }
                >
                  {shipping === 0
                    ? "FREE"
                    : `₹${shipping}`}
                </strong>
              </div>
            </div>

            {/* =================================================
                ITEMS
            ================================================= */}
            <div
              className={
                styles.reviewBlock
              }
            >
              <div
                className={
                  styles.reviewItems
                }
              >
                {selectedCartItems.map(
                  (
                    item,
                    index
                  ) => {
                    const product =
                      item.product ||
                      {};

                    const productName =
                      getDisplayValue(
                        item.product_name ||
                          product.name ||
                          item.name
                      ) ||
                      "Product";

                    const image =
                      product.thumb_image ||
                      product.image ||
                      product.thumbnail ||
                      product.product_image ||
                      item.image ||
                      "";

                    const price =
                      getProductPrice(
                        product,
                        item
                      );

                    const quantity =
                      Number(
                        item.quantity ||
                          item.qty ||
                          1
                      );

                    // =====================================
                    // VARIANT DETAILS
                    // =====================================
                    const variant =
                      item?.variant ||
                      {};

                    // SIZE
                    const size =
                      item?.selectedSize ||
                      variant?.size ||
                      variant?.size_name ||
                      variant?.size?.name ||
                      "";

                    // COLOR
                    const color =
                      item?.selectedColor ||
                      variant?.color_name ||
                      variant?.color ||
                      variant?.color?.name ||
                      "";

                    // =====================================
                    // ORIGINAL PRICE
                    // =====================================
                    const originalPrice =
                      Number(
                        variant?.price ??
                          product?.price ??
                          item?.price ??
                          0
                      );

                    // =====================================
                    // OFFER PRICE
                    // =====================================
                    const offerPrice =
                      variant?.offer_price !==
                        null &&
                      variant?.offer_price !==
                        undefined &&
                      variant?.offer_price !==
                        ""
                        ? Number(
                            variant.offer_price
                          )
                        : null;

                    const hasOffer =
                      offerPrice !==
                        null &&
                      offerPrice > 0 &&
                      originalPrice > 0 &&
                      offerPrice <
                        originalPrice;

                    const finalPrice =
                      hasOffer
                        ? offerPrice
                        : originalPrice;

                    const discountPercent =
                      hasOffer
                        ? Math.round(
                            ((originalPrice -
                              offerPrice) /
                              originalPrice) *
                              100
                          )
                        : 0;

                    return (
                      <div
                        className={
                          styles.reviewItem
                        }
                        key={
                          item.id ||
                          product.id ||
                          index
                        }
                      >
                        {/* IMAGE */}
                        <div
                          className={
                            styles.reviewItemImage
                          }
                        >
                          {image ? (
                            <img
                              src={getImageUrl(
                                image
                              )}
                              alt={
                                productName
                              }
                            />
                          ) : (
                            <div
                              className={
                                styles.noImage
                              }
                            >
                              🛍️
                            </div>
                          )}
                        </div>

                        {/* INFO */}
                        <div
                          className={
                            styles.reviewItemInfo
                          }
                        >
                          <strong
                            className={
                              styles.reviewProductName
                            }
                          >
                            {
                              productName
                            }
                          </strong>

                          {/* SIZE + COLOR */}
                          {(size ||
                            color) && (
                            <div
                              className={
                                styles.reviewVariantInfo
                              }
                            >
                              {size && (
                                <span
                                  className={
                                    styles.reviewVariantChip
                                  }
                                >
                                  <span
                                    className={
                                      styles.reviewVariantLabel
                                    }
                                  >
                                    Size :
                                  </span>

                                  <span
                                    className={
                                      styles.reviewVariantValue
                                    }
                                  >
                                    {getDisplayValue(
                                      size
                                    )}
                                  </span>
                                </span>
                              )}

                              {color && (
                                <span
                                  className={
                                    styles.reviewVariantChip
                                  }
                                >
                                  <span
                                    className={
                                      styles.reviewVariantLabel
                                    }
                                  >
                                    Color :
                                  </span>

                                  <span
                                    className={
                                      styles.reviewVariantValue
                                    }
                                  >
                                    {getDisplayValue(
                                      color
                                    )}
                                  </span>
                                </span>
                              )}
                            </div>
                          )}

                          {/* QUANTITY */}
                          <span
                            className={
                              styles.reviewQuantity
                            }
                          >
                            Qty:{" "}
                            {
                              quantity
                            }
                          </span>
                        </div>

                        {/* PRICE */}
                        <div
                          className={
                            styles.reviewItemPrice
                          }
                        >
                          <div
                            className={
                              styles.reviewFinalPrice
                            }
                          >
                            ₹
                            {(
                              finalPrice *
                              quantity
                            ).toLocaleString(
                              "en-IN"
                            )}
                          </div>

                          {hasOffer && (
                            <div
                              className={
                                styles.reviewPriceMeta
                              }
                            >
                              <span
                                className={
                                  styles.reviewOriginalPrice
                                }
                              >
                                ₹
                                {(
                                  originalPrice *
                                  quantity
                                ).toLocaleString(
                                  "en-IN"
                                )}
                              </span>

                              <span
                                className={
                                  styles.reviewDiscount
                                }
                              >
                                {
                                  discountPercent
                                }
                                % OFF
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }
                )}
              </div>
            </div>
          </div>
        </div>

        {/* =================================================
            RIGHT COLUMN - ORDER SUMMARY
        ================================================= */}
        <aside
          className={
            styles.orderSummarySection
          }
        >
          <div
            className={
              styles.summaryHeader
            }
          >
            <div>
              <h2>
                Order Summary
              </h2>

              <span>
                {
                  selectedCount
                }{" "}
                product
                {selectedCount !==
                1
                  ? "s"
                  : ""}
              </span>
            </div>
          </div>

          {/* =================================================
              PRICE DETAILS
          ================================================= */}
          <div
            className={
              styles.priceDetails
            }
          >
            <div
              className={
                styles.summaryRow
              }
            >
              <span>
                Subtotal (
                {totalQty} item
                {totalQty > 1
                  ? "s"
                  : ""}
                )
              </span>

              <strong>
                ₹
                {subtotal.toLocaleString(
                  "en-IN"
                )}
              </strong>
            </div>

            <div
              className={
                styles.summaryRow
              }
            >
              <span>
                Shipping
              </span>

              <strong
                className={
                  styles.freeShipping
                }
              >
                {shipping === 0
                  ? "FREE"
                  : `₹${shipping.toLocaleString(
                      "en-IN"
                    )}`}
              </strong>
            </div>

            <div
              className={
                styles.summaryRow
              }
            >
              <span>
                Tax
              </span>

              <strong>
                ₹
                {tax.toLocaleString(
                  "en-IN"
                )}
              </strong>
            </div>
          </div>

          {/* =================================================
              TOTAL
          ================================================= */}
          <div
            className={
              styles.summaryTotal
            }
          >
            <span>
              Total Amount
            </span>

            <strong>
              ₹
              {total.toLocaleString(
                "en-IN"
              )}
            </strong>
          </div>

          {/* =================================================
              SAVINGS
          ================================================= */}
          {subtotal >
            total && (
            <div
              className={
                styles.savingsMessage
              }
            >
              🎉 You are saving ₹
              {(
                subtotal -
                total
              ).toLocaleString(
                "en-IN"
              )}
            </div>
          )}

          {/* =================================================
              PLACE ORDER
          ================================================= */}
          <button
            type="button"
            className={
              styles.payButton
            }
            onClick={
              handlePlaceOrder
            }
            disabled={
              isOrderPlacePending ||
              !selectedPayment
            }
          >
            {isOrderPlacePending ? (
              <>
                <span
                  className={
                    styles.btn_spinner
                  }
                ></span>

                Processing...
              </>
            ) : (
              <>
                Place Order

                <span>
                  →
                </span>
              </>
            )}
          </button>

          {/* =================================================
              TERMS
          ================================================= */}
          <p
            className={
              styles.paymentTerms
            }
          >
            By placing your
            order, you agree to
            our{" "}
            <button
              type="button"
              className={
                styles.termsLink
              }
              onClick={() =>
                navigate(
                  "/terms-and-conditions"
                )
              }
            >
              Terms & Conditions
            </button>{" "}
            and{" "}
            <button
              type="button"
              className={
                styles.termsLink
              }
              onClick={() =>
                navigate(
                  "/privacy-policy"
                )
              }
            >
              Privacy Policy
            </button>
            .
          </p>

          {/* =================================================
              SECURITY
          ================================================= */}
          <div
            className={
              styles.securityBox
            }
          >
            <div
              className={
                styles.securityIcon
              }
            >
              🛡️
            </div>

            <div>
              <strong>
                Safe & Secure
                Payments
              </strong>

              <p>
                Your payment
                information is
                protected
              </p>
            </div>
          </div>

          {/* =================================================
              TRUST
          ================================================= */}
          <div
            className={
              styles.trustList
            }
          >
            <div
              className={
                styles.trustItem
              }
            >
              <span>
                ↩
              </span>

              <div>
                <strong>
                  Easy Returns
                </strong>

                <p>
                  7 days return
                  policy
                </p>
              </div>
            </div>

            <div
              className={
                styles.trustItem
              }
            >
              <span>
                ◉
              </span>

              <div>
                <strong>
                  24/7 Support
                </strong>

                <p>
                  We are here to
                  help
                </p>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
};

export default CheckoutPayment;